import { KPI, Tab } from '@/types/common';

export interface Target {
  id?: number;
  orgId?: number;
  name?: string;
  role?: string;
  industry?: string;
  country?: string;
  language?: string;
  monthlyAppointmentsTarget?: number;
  monthlyLeadsTarget?: number;
  monthlyRevenueTarget?: number;
  createdAt?: Date;
}

export enum PitchStrategy {
  CONSULTATIVE = 'Consultative',
  ROI_FOCUSED = 'ROI-Focused',
  AUTHORITY_EXPERT = 'Authority / Expert',
  CHALLENGER = 'Challenger',
  RELATIONSHIP = 'Relationship',
  DIRECT_ACTION = 'Direct / Action',
}

export interface Pitch {
  id?: number;
  orgId?: number;
  targetId?: number;
  target?: {
    id: number;
    name: string;
    role?: string;
    industry?: string;
  };
  targetLabel?: string; // Computed from target relation
  strategy?: PitchStrategy;
  text?: string; // May not exist in backend yet
  version?: number;
  active?: boolean;
  createdAt?: Date;
}

export interface SalesData {
  kpis: KPI[];
  tabs: Tab[];
  targets: Target[];
  pitches: Pitch[];
}

export enum LeadStatus {
  NEW = 'new',
  ENGAGED = 'engaged',
  PITCHED = 'pitched',
  MEETING_BOOKED = 'meeting_booked',
  WON = 'won',
  LOST = 'lost',
}

export enum OutreachChannel {
  LINKEDIN = 'linkedin',
  EMAIL = 'email',
  WHATSAPP = 'whatsapp',
}

export enum OutreachResult {
  NO_REPLY = 'no_reply',
  INTERESTED = 'interested',
  NOT_INTERESTED = 'not_interested',
  BOUNCED = 'bounced',
  BOOKED = 'booked',
}

export interface SxmLead {
  id: string;
  orgId?: number;
  email: string;
  fullName?: string;
  companyName?: string;
  title?: string;
  country?: string;
  language?: string;
  linkedinUrl?: string;
  status: LeadAutomationStatus;
  createdAt: string | Date;
  updatedAt?: string | Date;
  platform?: string;
  sourceUrl?: string;
}

export enum LeadAutomationStatus {
  NEW = 'new',
  ATTEMPT_1_SENT = 'attempt_1_sent',
  ATTEMPT_2_SENT = 'attempt_2_sent',
  ATTEMPT_3_SENT = 'attempt_3_sent',
  BOOKED = 'booked',
  CANCELED = 'canceled',
  INACTIVE = 'inactive',
  ENGAGED = 'engaged',
  PITCHED = 'pitched',
  WON = 'won',
}

export interface SxmOutreach {
  id: number;
  orgId: number;
  leadId?: string; // UUID from MxmLeads
  userId?: string;
  channel?: OutreachChannel;
  message?: string;
  sentAt: Date;
  result?: OutreachResult;
  lead?: SxmLead; // Populated when needed
}

export interface SxmAppointment {
  id: string;
  orgId?: number;
  leadId: string;
  lead?: SxmLead;
  userId: string;
  scheduledAt: Date;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes?: string;
}

export enum OutreachLogStatus {
  SENT = 'sent',
  FAILED = 'failed',
  SKIPPED = 'skipped'
}

export interface SxmOutreachLog {
  id: string;
  leadId: string;
  lead?: {
    id: string;
    email?: string;
    fullName?: string;
    companyName?: string;
    title?: string;
    platform?: string;
    mxmPost?: {
      id: number;
      title: string;
    };
    mxmUpload?: {
      id: string;
      title: string;
      category?: string;
    };
    mxmLeadCapturePage?: {
      id: string;
      title: string;
    };
    sourceUrl?: string;
  };
  userId: string;
  attemptNumber: number;
  email?: string;
  subject?: string;
  templateUsed?: string;
  bookingLinkIncluded: boolean;
  status: OutreachLogStatus;
  errorMessage?: string;
  sentAt: string | Date;
}

export type TableType = 'leads' | 'outreach' | 'appointments';

export interface SxmBooking {
  id: string; // uuid
  leadId: string;
  lead?: {
    fullName?: string;
    email?: string;
    companyName?: string;
  };
  userId: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  status: string;
  attendeeStatus?: string;
  htmlLink?: string;
  meetingLink?: string;
  webLink?: string; // Fallback or alias
  createdAt: Date;
}

